// Reusable anchored popover — the portal-into-scroll-container pattern proven on
// icon-menu.
//
// Renders its trigger inline and PORTALS the menu INTO the nearest scroll
// container, positioned `absolute` in that container's content coordinates. The
// browser then scrolls the menu natively in lockstep with the trigger — zero JS
// on scroll. A position:fixed menu re-anchored on every scroll event trails the
// compositor by a frame and visibly "jiggles"; this doesn't. It also escapes any
// `overflow-hidden` card between the trigger and the scroller, and is clipped by
// the scroller instead of riding over sticky nav.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { getScrollParent, usePrefersReducedMotion } from '@clickguard/design-system'
import { acquirePositioned, releasePositioned } from './positioned-lease'

export interface AnchoredPopoverProps {
	/** Inline trigger. `toggle` flips the menu; `open` reflects its state. */
	renderTrigger: (toggle: () => void, open: boolean) => ReactNode
	/** Menu content. `close` dismisses the popover (call after a selection). */
	children: (close: () => void) => ReactNode
	/** Menu width in px (also used to right-align). */
	width?: number
	/** Anchor the menu's left or right edge to the trigger. */
	align?: 'left' | 'right'
}

export const AnchoredPopover = ({ renderTrigger, children, width = 240, align = 'left' }: AnchoredPopoverProps) => {
	const reduced = usePrefersReducedMotion()
	const [open, setOpen] = useState(false)
	const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
	const triggerRef = useRef<HTMLDivElement>(null)
	const popRef = useRef<HTMLDivElement>(null)
	// The scroll container we portal into. It's flipped to `position: relative` via
	// a shared ref-counted lease (positioned-lease.ts) so many popovers can share
	// one container without clobbering each other's anchor or leaking the mutation.
	const scRef = useRef<HTMLElement | null>(null)
	const heldRef = useRef(false)
	// Mirror `open` in a ref so onExitComplete / unmount cleanup read the latest
	// value without being pinned to a stale render closure.
	const openRef = useRef(open)
	openRef.current = open

	const acquireLease = (sc: HTMLElement) => {
		if (heldRef.current) return
		acquirePositioned(sc)
		heldRef.current = true
	}
	const releaseLease = () => {
		if (!heldRef.current) return
		const sc = scRef.current
		if (sc) releasePositioned(sc)
		heldRef.current = false
	}

	const place = useCallback(() => {
		const trigger = triggerRef.current
		const sc = scRef.current
		if (!trigger || !sc) return
		const tr = trigger.getBoundingClientRect()
		const sr = sc.getBoundingClientRect()
		// Trigger position expressed in the container's scrollable content space, so
		// the absolutely-positioned menu rides the content as it scrolls.
		const top = tr.bottom - sr.top + sc.scrollTop + 6
		const left =
			align === 'right'
				? tr.right - sr.left + sc.scrollLeft - width
				: tr.left - sr.left + sc.scrollLeft
		setPos({ top: Math.round(top), left: Math.round(Math.max(8, left)) })
	}, [align, width])

	const toggle = () => {
		if (open) {
			setOpen(false)
			return
		}
		const sc = getScrollParent(triggerRef.current, document.scrollingElement as HTMLElement)
		scRef.current = sc
		// Take a lease so the container is a containing block for the absolute menu.
		acquireLease(sc)
		// NOTE: do NOT place() here. Positioning happens in the layout effect below
		// so the FIRST painted frame is already at the anchor. Measuring in the click
		// handler leaves the stale `pos` from the previous open on screen for a frame
		// (the menu "jumps from elsewhere, then snaps"); useLayoutEffect runs after
		// the open render but before paint, so the correct position is committed first.
		setOpen(true)
	}

	// Measure + position the moment `open` flips true (or align/width change while
	// open), before the browser paints.
	useLayoutEffect(() => {
		if (open) place()
	}, [open, place])

	useEffect(() => {
		if (!open) return
		const onDoc = (e: MouseEvent) => {
			const t = e.target as Node
			if (!triggerRef.current?.contains(t) && !popRef.current?.contains(t)) setOpen(false)
		}
		const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
		// No scroll handler — the menu lives in the scroll container's content and
		// moves with it natively (that's what kills the jiggle). Recompute only on
		// resize (layout reflow).
		const onResize = () => place()
		document.addEventListener('mousedown', onDoc)
		document.addEventListener('keydown', onEsc)
		window.addEventListener('resize', onResize)
		return () => {
			document.removeEventListener('mousedown', onDoc)
			document.removeEventListener('keydown', onEsc)
			window.removeEventListener('resize', onResize)
		}
	}, [open, place])

	// Release the lease if we unmount while still open (AnimatePresence is torn
	// down with us, so its onExitComplete never fires — this prevents a leaked
	// `position: relative` on the shared container).
	useEffect(() => () => releaseLease(), [])

	return (
		<div ref={triggerRef} className="relative inline-block">
			{renderTrigger(toggle, open)}
			{pos &&
				scRef.current &&
				createPortal(
					<AnimatePresence
						// Release the lease ONLY after the exit finishes — restoring the
						// container mid-exit (while the menu is still mounted and absolute)
						// swaps its containing block, re-resolving top/left against a new
						// origin so it visibly jumps left+down before fading. Guarded by
						// openRef: if we've been reopened during the 160ms exit, keep the
						// lease.
						onExitComplete={() => {
							if (!openRef.current) releaseLease()
						}}
					>
						{open && (
							// Pop from the trigger corner: fade + scale up from 0.9, pivoted at the
							// anchored edge, so it unfurls from the trigger. No `y` slide (a
								// translate reads as dropping in from above). Snappy ease-out-expo.
							<motion.div
								ref={popRef}
								className="z-[60]"
								style={{ position: 'absolute', top: pos.top, left: pos.left, width, transformOrigin: align === 'right' ? 'top right' : 'top left' }}
								initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.9 }}
								animate={reduced ? { opacity: 1 } : { opacity: 1, scale: 1 }}
								exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.94 }}
								transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
							>
								{children(() => setOpen(false))}
							</motion.div>
						)}
					</AnimatePresence>,
					scRef.current
				)}
		</div>
	)
}
